import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tour } from '@/components/wingo/ui/tour';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import { authApi } from 'api/auth';
import { buildTourSteps, NARROW_QUERY, tourLabels } from './tourSteps';

/**
 * Turul ghidat al portalului (Wingo UI `tour`).
 *
 * - Pornește singur o dată, la prima intrare a unui cont (`onboardingTourCompletedAt` null din /auth/me),
 *   dar niciodată într-o sesiune deschisă din ERP (impersonare) — acolo utilizatorul TLS nu e clientul.
 * - Încheiat sau sărit → POST /user-settings/onboarding-tour; backend-ul scrie data doar prima dată
 *   și ignoră sesiunile de impersonare. Reluarea din „Ajutor” nu mai atinge marcajul.
 * - Pe telefon cardul rămâne lângă țintă (`mobileLayout="popover"`), nu andocat jos.
 */

interface OnboardingTourContextValue {
  /** Pornește turul de la primul pas (din „Ajutor” sau din meniul contului). */
  startTour: () => void;
  isOpen: boolean;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

const DASHBOARD_PATH = '/app/dashboard';
const AUTO_START_DELAY_MS = 700;

function waitForElement(selector: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (document.querySelector(selector) || Date.now() - started > timeoutMs) resolve();
      else window.setTimeout(tick, 100);
    };
    tick();
  });
}

export function OnboardingTourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { currentLanguage } = useLanguage();
  const { direction, hasBothDirections } = useDirection();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  // cheie nouă la fiecare pornire: Tour își reia pasul de la 0
  const [run, setRun] = useState(0);
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  const autoStartHandled = useRef(false);
  const marked = useRef(false);

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const ensureDashboard = useCallback(async () => {
    if (!pathRef.current.startsWith(DASHBOARD_PATH)) navigate(DASHBOARD_PATH);
    await waitForElement('.stat-grid', 2500);
  }, [navigate]);

  const startTour = useCallback(() => {
    setIsNarrow(window.matchMedia(NARROW_QUERY).matches);
    setRun((value) => value + 1);
    setOpen(true);
  }, []);

  // Prima intrare a contului: pornește o singură dată pe sesiunea de browser.
  useEffect(() => {
    if (autoStartHandled.current || !user) return;
    if (user.impersonated || user.onboardingTourCompletedAt) {
      autoStartHandled.current = true;
      return;
    }
    autoStartHandled.current = true;
    // fără cleanup: o actualizare a lui `user` (ex. avatar) în intervalul ăsta nu trebuie să anuleze turul
    window.setTimeout(startTour, AUTO_START_DELAY_MS);
  }, [user, startTour]);

  const markFinished = useCallback(
    (outcome: 'completed' | 'skipped') => {
      if (!user || user.impersonated || user.onboardingTourCompletedAt || marked.current) return;
      marked.current = true;
      authApi.markOnboardingTour(outcome).catch((error) => {
        // fără marcaj turul repornește la următoarea intrare — mai bine decât să-l pierdem
        marked.current = false;
        console.warn('[tour] marcarea turului a eșuat', error?.response?.status);
      });
    },
    [user],
  );

  const lang = currentLanguage === 'en' ? 'en' : 'ro';
  const steps = useMemo(
    () => buildTourSteps({ lang, direction, hasBothDirections, isNarrow, ensureDashboard }),
    // pașii se reconstruiesc doar la o pornire nouă sau la schimbarea limbii/direcției
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, direction, hasBothDirections, isNarrow, run],
  );
  const labels = useMemo(() => tourLabels(lang), [lang]);

  const value = useMemo(() => ({ startTour, isOpen: open }), [startTour, open]);

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
      {run > 0 && (
        <Tour
          key={run}
          steps={steps}
          open={open}
          onOpenChange={setOpen}
          onComplete={() => markFinished('completed')}
          onSkip={() => markFinished('skipped')}
          labels={labels}
          tone="brand"
          showProgress="fraction"
          mobileLayout="popover"
        />
      )}
    </OnboardingTourContext.Provider>
  );
}

export function useOnboardingTour(): OnboardingTourContextValue {
  const context = useContext(OnboardingTourContext);
  if (!context) throw new Error('useOnboardingTour must be used within OnboardingTourProvider');
  return context;
}
